/**
 * Admin « Utilisateurs » — comptes NON CONFIRMÉS (migration 0317) : inscription
 * jamais terminée (email jamais vérifié ou aucun profil, ex. adresse mal tapée).
 * Ils n'apparaissent pas dans la liste principale (qui part des profils) :
 * cette section les montre, repliée, avec une suppression par compte. Le
 * serveur (`admin-user-access`, action delete) revérifie l'état du compte.
 */

import { useState } from 'react';
import { ChevronDown, Trash2, UserX } from 'lucide-react';
import { Button, cn } from '@jeraisonne/ui';
import {
  adminAccessErrorMessage,
  pendingAccountLabel,
  useDeleteUser,
  usePendingAccounts,
  type PendingAccount,
} from '@/lib/admin/users';

function formatDate(iso: string) {
  return new Intl.DateTimeFormat('fr-FR', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'Europe/Paris',
  }).format(new Date(iso));
}

export function PendingAccountsSection() {
  const pending = usePendingAccounts();
  const [open, setOpen] = useState(false);
  const list = pending.data ?? [];
  if (pending.isLoading || pending.error || list.length === 0) return null;

  return (
    <section className="border-ink-200 ocean:bg-white/5 rounded-2xl border bg-white">
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        className="flex min-h-[2.75rem] w-full items-center justify-between gap-3 p-4 text-left active:scale-[0.99]"
      >
        <span className="flex items-center gap-2">
          <UserX className="text-ink-500 h-5 w-5" aria-hidden />
          <span>
            <span className="font-display text-ink-900 block text-sm font-bold">
              Comptes non confirmés ({list.length})
            </span>
            <span className="text-ink-500 block text-xs">
              Inscription jamais terminée : absents de la liste ci-dessous.
            </span>
          </span>
        </span>
        <ChevronDown
          aria-hidden
          className={cn('text-ink-400 h-5 w-5 shrink-0 transition-transform', open && 'rotate-180')}
        />
      </button>
      {open && (
        <ul className="space-y-2 px-4 pb-4">
          {list.map((a) => (
            <li key={a.id}>
              <PendingAccountRow account={a} />
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function PendingAccountRow({ account }: { account: PendingAccount }) {
  const del = useDeleteUser();
  const [error, setError] = useState<string | null>(null);

  async function onDelete() {
    if (
      !window.confirm(
        `Supprimer définitivement le compte non confirmé ${account.email ?? account.id} ?\n\nCette action est irréversible.`,
      )
    ) {
      return;
    }
    setError(null);
    try {
      await del.mutateAsync(account.id);
    } catch (e) {
      setError(adminAccessErrorMessage(e instanceof Error ? e.message : ''));
    }
  }

  return (
    <div className="border-ink-100 flex flex-col gap-2 rounded-xl border p-3 sm:flex-row sm:items-center sm:justify-between">
      <div className="min-w-0">
        <p className="text-ink-900 truncate text-sm font-semibold">{account.email ?? '—'}</p>
        <p className="text-ink-500 text-xs">
          {pendingAccountLabel(account)} · créé le {formatDate(account.created_at)}
        </p>
        {error && (
          <p role="alert" className="text-error-600 mt-1 text-xs">
            {error}
          </p>
        )}
      </div>
      <Button
        variant="outline"
        className="border-error-300 text-error-700 min-h-[2.75rem] w-full shrink-0 sm:w-auto"
        disabled={del.isPending}
        onClick={() => void onDelete()}
        aria-label={`Supprimer ${account.email ?? 'ce compte'}`}
      >
        <Trash2 className="h-4 w-4" aria-hidden />
        {del.isPending ? 'Suppression…' : 'Supprimer'}
      </Button>
    </div>
  );
}
